import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { Eye, EyeOff, Lock, Mail, AlertCircle, Zap, Shield, Cpu, Palette, Rocket, Music, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'
import CosmicButton from '@/components/ui/CosmicButton'
import CosmicInput from '@/components/ui/CosmicInput'
import CosmicCard, { HolographicCard } from '@/components/ui/CosmicCard'
import ParticleSystem, { EnergyFlow, ScanLines } from '@/components/ui/ParticleSystem'
import { staggerContainer, staggerItem, pageTransition } from '@/lib/animations'

const Login: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  
  const { login, user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const from = location.state?.from?.pathname || '/'

  // Redirect authenticated users to their dashboard
  useEffect(() => {
    if (!authLoading && user) {
      console.log('🔄 Login component: User authenticated, determining redirect')
      console.log('👤 User role:', user.role, 'Tenant ID:', user.tenantId)
      
      let redirect = '/'
      // Prioritize tenantId over role for routing decisions
      if (user?.tenantId === 'creator') {
        redirect = '/creator'
        console.log('🎯 Redirecting to creator dashboard')
      } else if (user?.tenantId === 'client') {
        redirect = '/client'
        console.log('🎯 Redirecting to Client dashboard')
      } else if (user?.tenantId === 'internal' || user?.role?.toLowerCase() === 'admin') {
        redirect = '/admin'
        console.log('🎯 Redirecting to admin dashboard')
      }
      
      console.log('🚀 Navigating to:', redirect)
      navigate(redirect, { replace: true })
    }
  }, [user, authLoading, navigate])

  // Show loading if auth is still loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-stellar-blue mx-auto"></div>
          <p className="mt-4 text-stellar-cyan font-mono">Initializing...</p>
        </div>
      </div>
    )
  }

  // Don't render login form if user is already authenticated
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-stellar-blue mx-auto"></div>
          <p className="mt-4 text-stellar-cyan font-mono">Redirecting...</p>
        </div>
      </div>
    )
  }

  // Test credentials for development
  const testCredentials = [
    { email: 'admin@cosmk.com', password: 'admin123', role: 'Admin', tenant: 'internal', icon: Shield },
    { email: 'client@cosmk.com', password: 'client123', role: 'Client', tenant: 'client', icon: Cpu },
    { email: 'creator@cosmk.com', password: 'creator123', role: 'Creator', tenant: 'creator', icon: Music }
  ]

  const handleTestLogin = (testCred: typeof testCredentials[0]) => {
    setEmail(testCred.email)
    setPassword(testCred.password)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !password) {
      toast.error('Please fill in all fields')
      return
    }

    setLoading(true)
    try {
      await login(email, password)
      toast.success('Login successful!')
      // The useEffect will handle the redirect
    } catch (error: any) {
      toast.error(error.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-cosmic-gradient relative overflow-hidden">
      {/* Cosmic Background Effects */}
      <ParticleSystem particleCount={50} />
      <EnergyFlow />
      <ScanLines />
      
      {/* Ambient Lighting */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-stellar-blue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-nebula-purple/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-stellar-cyan/5 rounded-full blur-3xl" />
      </div>

      <motion.div 
        className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative z-10"
        variants={pageTransition}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <motion.div 
          className="max-w-md w-full space-y-8"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div variants={staggerItem}>
            <div className="text-center">
              <motion.div
                className="flex justify-center mb-6"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <Music className="w-16 h-16 text-stellar-blue" />
              </motion.div>
              
              <motion.h2 
                className="text-4xl font-display font-bold text-white glow-text mb-2"
                animate={{ textShadow: ["0 0 10px var(--stellar-blue)", "0 0 20px var(--stellar-blue)", "0 0 10px var(--stellar-blue)"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                COSMK VAULT
              </motion.h2>
              
              <p className="text-stellar-cyan font-mono uppercase tracking-wider">
                DIGITAL RIGHTS MANAGEMENT PLATFORM
              </p>
              
              <motion.div 
                className="mt-4 flex items-center justify-center gap-2"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="w-2 h-2 bg-stellar-green rounded-full animate-pulse" />
                <span className="text-xs font-mono text-stellar-green uppercase tracking-wider">
                  Hub Live
                </span>
              </motion.div>
            </div>
          </motion.div>
          
          {/* Login Form */}
          <motion.div variants={staggerItem}>
            <HolographicCard
              title="Access Control"
              subtitle="Enter your credentials"
              icon={<Shield />}
            >
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <CosmicInput
                    type="email"
                    label="Email Address"
                    placeholder="Enter your email"
                    value={email}
                    onChange={setEmail}
                    icon={<Mail />}
                    required
                  />

                  <div className="relative">
                    <CosmicInput
                      type={showPassword ? 'text' : 'password'}
                      label="Password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={setPassword}
                      icon={<Lock />}
                      required
                    />
                    
                    <motion.button
                      type="button"
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-stellar-cyan hover:text-stellar-blue transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </motion.button>
                  </div>
                </div>

                <CosmicButton
                  type="submit"
                  variant="primary"
                  loading={loading}
                  icon={<Sparkles />}
                  className="w-full"
                >
                  {loading ? 'AUTHENTICATING...' : 'ACCESS HUB'}
                </CosmicButton>

                <div className="text-center">
                  <motion.a 
                    href="#" 
                    className="text-sm font-mono text-stellar-cyan hover:text-stellar-blue transition-colors"
                    whileHover={{ scale: 1.05 }}
                  >
                    Forgot your password?
                  </motion.a>
                </div>
              </form>
            </HolographicCard>
          </motion.div>

          {/* Development Test Accounts */}
          {import.meta.env.DEV && (
            <motion.div variants={staggerItem}>
              <CosmicCard
                title="Development Test Accounts"
                subtitle="Click to auto-fill credentials"
                icon={<AlertCircle />}
                variant="data"
              >
                <div className="space-y-3">
                  {testCredentials.map((cred, index) => {
                    const Icon = cred.icon
                    return (
                      <motion.button
                        key={index}
                        onClick={() => handleTestLogin(cred)}
                        className="w-full text-left p-3 bg-cosmic-dark/50 rounded-lg border border-stellar-blue/20 hover:border-stellar-blue/50 transition-all duration-300 group"
                        whileHover={{ scale: 1.02, x: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="flex items-center gap-3">
                          <motion.div
                            className="text-stellar-blue"
                            whileHover={{ rotate: 360 }}
                            transition={{ duration: 0.5 }}
                          >
                            <Icon className="w-5 h-5" />
                          </motion.div>
                          <div className="flex-1">
                            <div className="font-mono text-white text-sm">{cred.role}</div>
                            <div className="font-mono text-stellar-cyan text-xs">{cred.email}</div>
                            <div className="font-mono text-stellar-cyan/70 text-xs">
                              Password: {cred.password}
                            </div>
                          </div>
                          <motion.div
                            className="w-2 h-2 bg-stellar-blue rounded-full opacity-0 group-hover:opacity-100"
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          />
                        </div>
                      </motion.button>
                    )
                  })}
                </div>
                
                <motion.p 
                  className="text-xs font-mono text-stellar-cyan/70 mt-3 text-center"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  Development environment - Test accounts available
                </motion.p>
              </CosmicCard>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </div>
  )
}

export default Login 